<template>
  <div>
    <v-layout
      class="mx-0"
      justify-space-between align-center
    >
      <div class="text-h5 font-weight-bold text-capitalize">
        {{ documentsType }} Documents
      </div>
      <v-flex xs5 md4>
        <v-text-field
          data-testid="doc-search-input"
          class="doc-search-input"
          v-model="searchTerm"
          append-icon="search"
          clear-icon="mdi-close-circle"
          @click:clear="clearSearch"
          @click:append="$emit('searchDocuments', searchTerm)"
          :label="documentsType === 'unsigned'
            ? 'Search unsigned documents...'
            : 'Search signed documents...'"
          filled
          hide-details
        />
      </v-flex>
    </v-layout>
    <v-data-table
      class="mt-3"
      disable-sort
      :loading="tableLoading"
      :headers="headers"
      :items="documentsFormatted"
      :options.sync="pagination"
      :server-items-length="documentsType === 'signed' ? totalSigned : totalUnsigned"
      :footer-props="{'items-per-page-options': [25, 50, 75, 100]}"
      :no-data-text="tableLoading ? 'Loading documents...' : 'No results found. Please try again.'"
      id="scroll-table"
    >
      <template slot="item" slot-scope="props">
        <tr>
        <td>{{ props.item.metadata.location_code }}</td>
        <td>{{ props.item.name }}</td>
        <td>{{ props.item.email }}</td>
        <td class="text-capitalize">{{ props.item.metadata.document_type }}</td>
        <td v-if="documentsType === 'unsigned'">{{ formatDate(props.item.last_viewed) }}</td>
        <td v-else>{{ formatDate(props.item.date_signed) }}</td>
        <td v-if="documentsType === 'unsigned'">
          <v-btn
            data-testid="doc-unsigned-email-url-modal-btn"
            @click="openEmailModal(props.item.signature_request_id)"
            class="doc-unsigned-email-url-modal-btn mr-3 primary"
          >
            Email
          </v-btn>
          <v-btn
            data-testid="doc-unsigned-delete-url-modal-btn"
            @click="openDeleteModal(props.item.signature_request_id)"
            class="doc-unsigned-delete-url-modal-btn mr-3 error"
          >
            Delete
          </v-btn>
        </td>
        <td v-else>
          <v-btn
            data-testid="doc-signed-email-modal-btn"
            @click="openEmailCompleteModal(props.item.signature_request_id)"
            class="doc-signed-email-modal-btn mr-3 primary"
          >
            Email Docs To...
          </v-btn>
          <v-btn
            data-testid="doc-download-file-btn"
            class="doc-download-file-btn mr-3 error"
            @click="downloadFile(props.item)"
            :loading="downloading && downloadId === props.item.signature_request_id"
            :disabled="downloading && downloadId !== props.item.signature_request_id"
          >
            Download
          </v-btn>
        </td>
        </tr>
      </template>
    </v-data-table>
    <application-modal
      v-model="emailModal"
      @cancel="closeEmailModal"
      :disabled="!emailAddress"
      :loading="loading"
    >
      <div slot="modal-title">Email Document</div>
      <div slot="modal-body">
        <div>Enter a valid email address for this document to be sent to.</div>
        <v-text-field
          data-testid="doc-email-url-input"
          class="doc-email-url-input py-6"
          v-model="emailAddress"
          label="Email Address"
          filled
          hide-details
          v-on:keydown.enter.prevent
        />
      </div>
      <v-btn
        data-testid="doc-email-url-confirm-btn"
        class="doc-email-url-confirm-btn primary"
        slot="create-button"
        @click="handleEmailRequest"
        :loading="loading"
        :disabled="!validEmail"
      >
        Confirm
      </v-btn>
    </application-modal>
    <application-modal
      v-model="emailCompleteModal"
      @cancel="closeEmailCompleteModal"
      :disabled="!emailAddress"
      :loading="loading"
    >
      <div slot="modal-title">Email Document</div>
      <div slot="modal-body">
        <div>Enter a valid email address for this document to be sent to.</div>
        <v-text-field
          data-testid="doc-email-complete-input"
          class="doc-email-complete-input py-6"
          v-model="emailAddress"
          label="Email Address"
          filled
          hide-details
          v-on:keydown.enter.prevent
        />
      </div>
      <v-btn
        data-testid="doc-email-complete-confirm-btn"
        class="doc-email-complete-confirm-btn primary"
        slot="create-button"
        @click="handleEmailCompleteRequest"
        :loading="loading"
        :disabled="!validEmail"
      >
        Confirm
      </v-btn>
    </application-modal>
    <application-modal
      v-model="deleteModal"
      @confirm="handleDeleteRequest"
      @cancel="closeDeleteModal"
      :loading="loading"
    >
      <div slot="modal-title">Delete Document</div>
      <div slot="modal-body">
        <div>Press "Confirm" to delete this document.</div>
      </div>
      <v-btn
        data-testid="delete-doc-table-confirm-btn"
        class="delete-doc-table-confirm-btn primary"
        slot="create-button"
        @click="handleDeleteRequest"
        :loading="loading"
      >
        Confirm
      </v-btn>
    </application-modal>
  </div>
</template>

<script>
import ApplicationModal from "@/components/ApplicationModal.vue";
import { mapState } from "vuex";
import moment from "moment";

export default {
  props: {
    documentsType: {
      type: String
    },
    locationCode: {
      type: String
    },
    tableLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      deleteModal: false,
      emailAddress: "",
      emailModal: false,
      emailCompleteModal: false,
      loading: false,
      search: "",
      pagination: {},
      selectedSignatureRequestId: "",
      downloadId: 0,
      downloading: false
    };
  },

  // ===== Methods ===== //
  methods: {
    openEmailModal(signatureRequestId) {
      this.emailModal = true;
      this.selectedSignatureRequestId = signatureRequestId;
    },

    openEmailCompleteModal(signatureRequestId) {
      this.emailCompleteModal = true;
      this.selectedSignatureRequestId = signatureRequestId;
    },

    async downloadFile(file) {
      this.toggleDownloadView(true, file.signature_request_id);
      try {
        await this.$store.dispatch("downloadEsignSignedDocument", file.signature_request_id);
      } finally {
        this.toggleDownloadView(false, 0);
      }
    },

    toggleDownloadView(downloading, downloadId) {
      this.downloading = downloading;
      this.downloadId = downloadId;
    },

    closeEmailModal() {
      this.emailAddress = "";
      this.emailModal = false;
      this.selectedSignatureRequestId = "";
    },

    closeEmailCompleteModal() {
      this.emailAddress = "";
      this.emailCompleteModal = false;
      this.selectedSignatureRequestId = "";
    },

    async handleEmailRequest() {
      const payload = {
        email: this.emailAddress,
        signatureRequestId: this.selectedSignatureRequestId,
        location_code: this.locationCode
      };
      this.toggleLoading(true);
      try {
        await this.$store.dispatch("emailEsignSignatureRequest", payload);
      } finally {
        this.toggleLoading(false);
        this.closeEmailModal();
      }
    },

    toggleLoading(loading) {
      this.loading = loading;
    },

    async handleEmailCompleteRequest() {
      const payload = {
        email: this.emailAddress,
        signatureRequestId: this.selectedSignatureRequestId,
        location_code: this.locationCode
      };
      this.toggleLoading(true);
      try {
        await this.$store.dispatch("emailCompletedEsignSignatureRequest", payload);
      } finally {
        this.toggleLoading(false);
        this.closeEmailCompleteModal();
      }
    },

    openDeleteModal(signatureRequestId) {
      this.deleteModal = true;
      this.selectedSignatureRequestId = signatureRequestId;
    },

    closeDeleteModal() {
      this.deleteModal = false;
      this.selectedSignatureRequestId = "";
    },

    async handleDeleteRequest() {
      this.toggleLoading(true);
      try {
        await this.$store.dispatch("deleteEsignSignatureRequest", this.selectedSignatureRequestId);
      } finally {
        this.toggleLoading(false);
        this.closeDeleteModal();
      }
    },

    formatDate(timestamp) {
      return timestamp ? moment.unix(timestamp).format("MM/DD/YYYY") : "N/A";
    },

    getName(signatureRequest) {
      return signatureRequest.signatures.filter((sigData) => sigData.signer_name)[0].signer_name;
    },

    getEmail(signatureRequest) {
      return signatureRequest.signatures.filter((sigData) => sigData.signer_name)[0].signer_email_address;
    },

    getDateSigned(signatureRequest) {
      return signatureRequest.signatures.filter((sigData) => sigData.signer_name)[0].signed_at;
    },

    getLastViewed(signatureRequest) {
      return signatureRequest.signatures.filter((sigData) => sigData.signer_name)[0].last_viewed_at;
    },

    clearSearch() {
      this.$store.dispatch("setEsignSearchTerm", { searchType: this.documentsType, searchTerm: "" });
      this.$emit("searchDocuments", null);
    }
  },
  computed: {
    ...mapState({
      signatureRequest: state => state.documentManager.signatureRequest,
      signedDocuments: state => state.documentManager.signedDocuments,
      signedSearchTerm: state => state.documentManager.signedSearchTerm,
      totalSigned: state => state.documentManager.totalSigned,
      totalUnsigned: state => state.documentManager.totalUnsigned,
      unsignedDocuments: state => state.documentManager.unsignedDocuments,
      unsignedSearchTerm: state => state.documentManager.unsignedSearchTerm
    }),
    headers() {
      return [
        {
          text: "Location Code",
          value: "metadata.location_code",
          sortable: false
        },
        {
          text: "Name",
          value: "name",
          sortable: false
        },
        {
          text: "Email Address",
          value: "email",
          sortable: false
        },
        {
          text: "Document Type",
          value: "metadata.document_type",
          sortable: false
        },
        {
          text: this.documentsType === "unsigned" ? "Last Viewed" : "Date Signed",
          value: this.documentsType === "unsigned" ? "last_viewed" : "date_signed",
          sortable: false
        },
        {
          text: "Actions...",
          value: "actions",
          sortable: false
        }
      ];
    },
    documentsFormatted() {
      const documentsToFormat = (this.documentsType === "signed")
        ? this.signedDocuments
        : this.unsignedDocuments;

      return documentsToFormat.map((sigReq) => {
        return {
          ...sigReq,
          name: this.getName(sigReq),
          email: this.getEmail(sigReq),
          date_signed: this.getDateSigned(sigReq),
          last_viewed: this.getLastViewed(sigReq),
          loading: false
        };
      });
    },
    searchTerm: {
      get() {
        return this.documentsType === "signed" ? this.signedSearchTerm : this.unsignedSearchTerm;
      },
      set(newVal) {
        /* istanbul ignore next */
        const payload = { searchTerm: newVal, searchType: this.documentsType };
        /* istanbul ignore next */
        this.$store.dispatch("setEsignSearchTerm", payload);
      }
    },
    validEmail() {
      let regex = /^\S+@\S+\.\S+$/;
      return regex.test(this.emailAddress);
    }
  },

  // ===== Watchers ===== //
  watch: {
    pagination: {
      handler() {
        const newPagination = {
          page: this.pagination.page,
          per_page: this.pagination.itemsPerPage
        };
        this.$emit("updatePagination", newPagination);
      },
      deep: true
    }
  },

  components: {
    ApplicationModal
  }
};
</script>
